import _ from 'lodash';
import { rgba } from 'polished';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { AddIcon, ListIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Callout,
	Container,
	Heading,
	Jumbo,
	Paragraph,
	QuillText,
	Separator,
	Skeleton,
	Snippet,
	Subheading,
	tokens,
	useAppScrollElement,
} from '@treams/treams-ui';

import CompetenciesGraph from './CompetenciesGraph';
import Competency, { MiniCompetencyLevel } from './Competency';
import { ISkillsAnalysis } from './SkillsAnalysis';

const NAV_HEIGHT = 48;

const SegmentIcon = styled(Box)`
	width: 24px;
	height: 24px;
	margin-right: 2px;

	svg path {
		fill: ${tokens.text.secondary};
	}
`;

const SegmentContainer = styled(Flex)<{ selected?: boolean }>`
	line-height: 30px;
	border-radius: 15px;
	border: 1px solid ${tokens.border.subtle};
	padding: 0px 10px;
	color: ${tokens.text.secondary};
	cursor: pointer;
	align-items: center;
	flex-grow: 0;
	flex-shrink: 0;

	${(props) =>
		props.selected
			? `
        background: ${rgba(props.theme.colors.primary, 0.25)};
        border-color: transparent;
        color: ${props.theme.colors.primary};

        ${SegmentIcon} svg path {
            fill: ${props.theme.colors.primary};
        }
        `
			: `
        &:hover {
            background: ${rgba(tokens.border.subtle, 0.5)};
        }
        `}
`;

function Segment(props: {
	icon?: ReactNode;
	title: string;
	selected?: boolean;
	onClick: () => void;
}) {
	return (
		<SegmentContainer mr={2} selected={props.selected} onClick={props.onClick}>
			{props.icon && <SegmentIcon>{props.icon}</SegmentIcon>}
			{props.title}
		</SegmentContainer>
	);
}

Segment.Skeleton = styled(Skeleton.DynamicBox)`
	height: 30px;
`;

enum CompetenciesFeedbackStatus {
	EmptyCompetencies,
	EmptyAppraisals,
	Active,
}

function feedbackStatus(
	competencies: graphql.JobCompetency[],
	appraisals: graphql.CompetencyAppraisal[],
) {
	if (competencies.length === 0) {
		return CompetenciesFeedbackStatus.EmptyCompetencies;
	}

	if (appraisals.length === 0) {
		return CompetenciesFeedbackStatus.EmptyAppraisals;
	}

	return CompetenciesFeedbackStatus.Active;
}

export default function Competencies(props: { skillsAnalysis: ISkillsAnalysis; owned?: boolean }) {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined);
	const location = useLocation();

	const scroller = useAppScrollElement();
	const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

	const { appraisals: allAppraisals, jobCompetencies, competencies } = props.skillsAnalysis;
	const status = feedbackStatus(jobCompetencies, allAppraisals);

	const minLevels = new Map<string, number>();
	const appraisals = new Map(
		allAppraisals.map((appraisal) => [appraisal.competency.id, appraisal.competencyLevelIndex]),
	);

	for (const competency of jobCompetencies) {
		minLevels.set(
			competency.competency.id,
			Math.max(competency.competencyLevel, minLevels.get(competency.id) ?? 0),
		);
	}

	function levelsForCompetency(competency: graphql.Competency) {
		const expected = minLevels.get(competency.id) ?? 0;
		const actual = appraisals.get(competency.id) ?? -1;

		return competency.levels.map((level, index) => {
			if (!level.enabled || actual < 0) {
				return null;
			}

			if (index <= actual) {
				return true;
			}

			if (index > expected) {
				return null;
			}

			return false;
		});
	}

	function scrollTo(index?: number) {
		if (!scroller) {
			return;
		}

		const section = index === undefined ? undefined : sectionRefs.current[index];

		if (index !== undefined && !section) {
			return;
		}

		const top = section
			? section.getBoundingClientRect().top -
				scroller.getBoundingClientRect().top +
				scroller.scrollTop -
				NAV_HEIGHT
			: 0;

		scroller.scrollTo({ top, behavior: 'smooth' });
	}

	const sectionCount = competencies.length;

	useEffect(() => {
		if (!scroller) {
			return;
		}

		function onScroll() {
			if (!scroller || scroller.scrollTop === 0) {
				setSelectedIndex(undefined);
				return;
			}

			const threshold = scroller.getBoundingClientRect().top + NAV_HEIGHT;

			const passed = sectionRefs.current
				.slice(0, sectionCount)
				.map((section, index) => [index, section?.getBoundingClientRect().top] as const)
				.filter(([, top]) => top !== undefined && top <= threshold);

			setSelectedIndex(passed.length ? passed[passed.length - 1][0] : undefined);
		}

		onScroll();
		scroller.addEventListener('scroll', onScroll, { passive: true });

		return () => scroller.removeEventListener('scroll', onScroll);
	}, [scroller, sectionCount]);

	const sortedCompetencies = _.sortBy(
		competencies,
		(competency) => localize(competency.title).text,
	);

	return (
		<>
			<Container pt={4} mb={-2}>
				<a href={t('skills.callout.link')} target="_blank" rel="noreferrer">
					<Callout color="blue">
						<Box>
							<strong>{t('skills.callout.new')}:</strong> {t('skills.callout.description')}
						</Box>
					</Callout>
				</a>
			</Container>
			<Jumbo
				title={
					props.skillsAnalysis.jobTitle
						? t('skills.jobTitle.pageTitle', {
								jobTitle: localize(props.skillsAnalysis.jobTitle.title).text,
							})
						: t('competencies.gap.title')
				}
				description={!props.skillsAnalysis.jobTitle ? t('competencies.gap.description') : undefined}
				button={
					props.owned && (
						<Button
							icon={<AddIcon />}
							title={t('competencies.gap.request')}
							link={`/feedback/request/competencies?return=${location.pathname}`}
						/>
					)
				}
				footer={
					<>
						{props.skillsAnalysis.jobTitle &&
							props.skillsAnalysis.jobTitle.description &&
							!QuillText.isEmpty(localize(props.skillsAnalysis.jobTitle.description).text) && (
								<Container mb={3}>
									<Snippet>
										<Paragraph>
											<QuillText value={localize(props.skillsAnalysis.jobTitle.description).text} />
										</Paragraph>
									</Snippet>
								</Container>
							)}
						<Separator />
					</>
				}
			/>

			<Box style={{ position: 'sticky', top: 0 }} backgroundColor="white">
				<Container py={2}>
					<Flex>
						<Segment
							title={t('competencies.gap.navigation.overview')}
							icon={<ListIcon />}
							selected={selectedIndex === undefined}
							onClick={() => scrollTo(undefined)}
						/>
						{sortedCompetencies.map((competency, index) => (
							<Segment
								key={competency.id}
								title={localize(competency.title).text}
								selected={selectedIndex === index}
								onClick={() => scrollTo(index)}
							/>
						))}
					</Flex>
				</Container>

				<Separator />
			</Box>

			<Box style={{ flexGrow: 1, flexShrink: 1 }}>
				<Box>
					<Container>
						<Flex style={{ position: 'sticky', top: 0, height: 0 }}>
							<Box flexGrow={1} flexShrink={1} />
							<Box flexGrow={0} flexShrink={0} mt={5}>
								<CompetenciesGraph
									data={sortedCompetencies.map((competency) => ({
										title: localize(competency.title).text,
										levels: levelsForCompetency(competency),
									}))}
									selectedIndex={selectedIndex}
									onSelect={scrollTo}
								/>
							</Box>
						</Flex>

						<Flex>
							<Box flexGrow={1} flexShrink={1}>
								<Box mt={4}>
									{status === CompetenciesFeedbackStatus.EmptyCompetencies && (
										<Box mb={2}>
											<Heading>{t('skills.graph.competencies.empty.title')}</Heading>
											<Paragraph>{t('skills.graph.competencies.empty.description')}</Paragraph>
										</Box>
									)}

									{props.owned && status === CompetenciesFeedbackStatus.EmptyAppraisals && (
										<Box mb={2}>
											<Heading>{t('skills.graph.appraisals.empty.title')}</Heading>
											<Paragraph>
												<Link to={`/feedback/request/competencies?return=${location.pathname}`}>
													{t('skills.graph.appraisals.empty.link')}
												</Link>{' '}
												{t('skills.graph.appraisals.empty.description')}
											</Paragraph>
										</Box>
									)}

									{status === CompetenciesFeedbackStatus.Active && (
										<>
											<Box mb={2}>
												<Heading>{t('skills.graph.instructions.title')}</Heading>
												<Paragraph>{t('skills.graph.instructions.description')}</Paragraph>
											</Box>
											<Flex py={0.5} alignItems="center">
												<MiniCompetencyLevel active={false} color="green" />
												<Paragraph ml={2}>{t('skills.graph.instructions.reached.label')}</Paragraph>
											</Flex>
											<Flex py={0.5} alignItems="center">
												<MiniCompetencyLevel active={false} color="yellow" />
												<Paragraph ml={2}>
													{t('skills.graph.instructions.unreached.label')}
												</Paragraph>
											</Flex>
											<Flex py={0.5} alignItems="center">
												<MiniCompetencyLevel active={false} color="slateBlue" />
												<Paragraph ml={2}>
													{t('skills.graph.instructions.inactive.label')}
												</Paragraph>
											</Flex>
										</>
									)}
								</Box>

								{sortedCompetencies.map((competency, i) => (
									<Box
										key={competency.id}
										ref={(element: HTMLDivElement | null) => {
											sectionRefs.current[i] = element;
										}}
										minHeight={
											i === competencies.length - 1 ? `calc(100vh - ${NAV_HEIGHT}px)` : undefined
										}
									>
										<Competency
											competency={competency}
											min={minLevels.get(competency.id) ?? 0}
											levels={levelsForCompetency(competency)}
											options={competency.levels}
											owned={props.owned}
										/>
									</Box>
								))}
							</Box>
							<Box width={336} flexGrow={0} flexShrink={0} ml={2} />
						</Flex>
					</Container>
				</Box>
			</Box>
		</>
	);
}

Competencies.Skeleton = function CompetenciesSkeleton() {
	return (
		<>
			<Jumbo.Skeleton />
			<Separator />
			<Container py={2}>
				<Flex>
					<Segment.Skeleton mr={2} />
					<Segment.Skeleton mr={2} />
					<Segment.Skeleton />
				</Flex>
			</Container>
			<Separator />
			<Container my={4}>
				<Subheading.Skeleton mb={2} />
				<Paragraph.Skeleton />
			</Container>
		</>
	);
};
